import { useEffect, useMemo } from 'react';
import type { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createInspection, CreateInspectionFieldsSchema } from '../../lib/api/inspections.gen';
import type { Subject } from '../../lib/api/subjects.gen';
import {
  CardActions, DateInput, DialogActions, Form, FormError, FormField, Grid, Modal, ModalBody, ModalFooter,
  ModalHeader, Select, Stack, useZodForm,
} from '../ui';
import { TypePicker } from './TypePicker';
import { useVocab } from '../../lib/vocab';
import { errorMessage } from '../../lib/errorMessage';

// This dialog only ever needs a preset's id/name/default-flag to build the
// picker below — never its form `schema` — so it types its own minimal shape
// rather than the full `ReportPreset` (story 4-2a: the library LIST that now
// feeds this prop, `TemplateListItem`, deliberately omits `schema`). `_id`
// is kept as the field name (rather than the list item's `id`) because it is
// also this dialog's OWN wire field going into `createInspection`'s
// `reportPresetId` — callers map their source's id onto it.
export interface PresetOption {
  _id: string;
  name: string;
  isDefault?: boolean;
}

interface CreateInspectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** The Sites this inspection could be against (P03 §0 — canonical subjects). */
  sites: Subject[];
  presets: PresetOption[];
  isLoadingSites?: boolean;
  onCreated?: (inspectionId: string) => void;
  onRequestCreateSite?: () => void;
}

// G-26 (story 2-95b): composed over the generated contract schema
// (`CreateInspectionFieldsSchema`) rather than hand-declared. `POST
// /v1/inspections` has been contract-first since story 5-2a — verified live
// against the backend route (`validateContract(inspectionContracts.endpoints
// .createInspection)`, `backend/src/routes/v1/inspection.route.js`), NOT
// legacy Joi as this file's own header comment used to assume before this
// story re-checked it (the stale assumption also drove story-map note
// 2-122, filed against an earlier, now-outdated state of the tree — the
// CREATE endpoint's own zod migration landed separately from the
// list/get-read family 2-120 still owns). `subjectId` is re-required via
// `.unwrap()` on the generated optional — the wire contract allows a legacy
// caller to send `propertyId` instead (P03 §6's compatibility window); this
// dialog has no such history and always sends `subjectId` (identical to the
// selected Site's `_id`), so the resolver should say so at field level.
// `scheduledFor`/`reportPresetId`/`inspectionTypeId` stay optional, matching
// this dialog's own long-standing "(optional)" copy.
const createInspectionFormSchema = CreateInspectionFieldsSchema.pick({
  subjectId: true,
  scheduledFor: true,
  reportPresetId: true,
  inspectionTypeId: true,
}).extend({
  subjectId: CreateInspectionFieldsSchema.shape.subjectId.unwrap(),
});

type CreateInspectionFormValues = z.input<typeof createInspectionFormSchema>;

const emptyValues: CreateInspectionFormValues = {
  subjectId: '',
  scheduledFor: '',
  reportPresetId: '',
  inspectionTypeId: '',
};

export function CreateInspectionDialog({
  isOpen,
  onClose,
  sites,
  presets,
  isLoadingSites = false,
  onCreated,
  onRequestCreateSite,
}: CreateInspectionDialogProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const form = useZodForm(createInspectionFormSchema, { defaultValues: emptyValues });

  // Vocabulary layer (P03 §5, story 3-2). Every record now carries its own
  // `domainKey`, so the noun is resolved per SELECTED record rather than from
  // the org's default pack — a website org and a building org read correctly on
  // the same screen.
  const { vocab } = useVocab();
  const selectedSiteId = form.watch('subjectId');
  const selectedSite = useMemo(() => sites.find((site) => site._id === selectedSiteId), [sites, selectedSiteId]);
  const domainKey = selectedSite?.domainKey ?? null;
  const subjectNoun = vocab('Subject', domainKey ?? undefined);
  const subjectLower = subjectNoun.toLowerCase();
  const subjectLowerPlural = vocab('Subjects', domainKey ?? undefined).toLowerCase();
  const unitsLower = vocab('Units', domainKey ?? undefined).toLowerCase();

  const hasSites = sites.length > 0;

  useEffect(() => {
    if (isOpen) {
      const defaultSite = sites[0]?._id ?? '';
      const defaultPreset = presets.find((preset) => preset.isDefault)?._id ?? '';
      form.reset({ subjectId: defaultSite, scheduledFor: '', reportPresetId: defaultPreset, inspectionTypeId: '' });
    }
  }, [isOpen, sites, presets, form]);

  const mutation = useMutation({
    mutationFn: createInspection,
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ['inspections'] });
      onCreated?.(response.data._id);
    },
  });

  // `mutation` (the useMutation() return value) is a new object every render, so
  // depending on it in an effect re-fires that effect on every render — a self-sustaining
  // "Maximum update depth exceeded" loop (2-30). `mutation.reset` is the stable bound
  // method the effect below actually calls; pull it out so the effect can depend on the
  // stable function instead of the ever-changing wrapper object.
  const resetMutation = mutation.reset;

  useEffect(() => {
    if (!isOpen) {
      form.reset(emptyValues);
      resetMutation();
    }
  }, [isOpen, resetMutation, form]);

  const handleSubmit = async (values: CreateInspectionFormValues) => {
    mutation.mutate({
      // The wire field keeps its legacy name for the compatibility window
      // (P03 §6); the VALUE is the canonical Subject id.
      subjectId: values.subjectId,
      ...(values.scheduledFor ? { scheduledFor: new Date(values.scheduledFor).toISOString() } : {}),
      ...(values.reportPresetId ? { reportPresetId: values.reportPresetId } : {}),
      ...(values.inspectionTypeId ? { inspectionTypeId: values.inspectionTypeId } : {}),
    });
  };

  const formDisabled = !hasSites || mutation.isPending || isLoadingSites;

  const presetOptions = useMemo(() => {
    return presets.map((preset) => ({ id: preset._id, label: preset.name }));
  }, [presets]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal onClose={onClose} label="Create inspection" size="lg">
      <ModalHeader title="Create inspection" onClose={onClose}>
        <p className="text-sm text-muted-foreground">{`Select a ${subjectLower} and the procedure to follow.`}</p>
      </ModalHeader>

      <ModalBody>
      {!hasSites && !isLoadingSites && (
        <Stack gap={2} align="stretch" className="mt-4 rounded-2xl border border-warning/40 bg-warning/10 px-3 py-3 text-sm text-warning">
          <p className="font-medium">{`Add a ${subjectLower} first before creating inspections.`}</p>
          {onRequestCreateSite ? (
            <CardActions
              align="start"
              size="sm"
              actions={[{ id: 'go-to-sites', label: `Go to ${subjectLowerPlural}`, variant: 'secondary', onClick: onRequestCreateSite }]}
            />
          ) : null}
        </Stack>
      )}
      {isLoadingSites && (
        <div className="mt-4 rounded-2xl border border-border/40 bg-white/40 px-3 py-3 text-sm text-muted-foreground dark:bg-white/10">
          {`Loading ${subjectLowerPlural}…`}
        </div>
      )}

      <Form form={form} onSubmit={handleSubmit} id="create-inspection-form" className="mt-6">
        <Grid columns={{ base: 1, md: 2 }} gap={4}>
          <FormField name="subjectId" label={subjectNoun} required>
            {(field) => (
              <Select
                data-testid="inspection-site"
                {...field}
                onChange={(event) => {
                  field.onChange(event);
                  form.setValue('inspectionTypeId', '');
                }}
                disabled={formDisabled || isLoadingSites}
              >
                <option value="" disabled>
                  {`Select ${subjectLower}…`}
                </option>
                {sites.map((site) => (
                  <option key={site._id} value={site._id}>
                    {site.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>

          <FormField name="scheduledFor" label="Scheduled for (optional)">
            {(field) => <DateInput {...field} disabled={mutation.isPending} />}
          </FormField>

          {/* The procedure (P03 §3.2, §5, §7.2). Filtered by the selected Site's
              domain and the caller's SERVER-resolved roles; a type the caller may
              not perform stays visible and disabled with its reason. */}
          <FormField name="inspectionTypeId" label="Inspection type" control="group">
            {() => (
              <div data-testid="inspection-type-picker">
                <TypePicker
                  domainKey={domainKey}
                  domainNoun={subjectNoun}
                  value={form.watch('inspectionTypeId') || null}
                  onChange={(typeId) => form.setValue('inspectionTypeId', typeId ?? '')}
                  disabled={mutation.isPending}
                />
              </div>
            )}
          </FormField>

          <div className="md:col-span-2">
            <FormField name="reportPresetId" label="Report preset (optional)">
              {(field) => (
                <Select {...field} disabled={mutation.isPending}>
                  <option value="">Use organization default</option>
                  {presetOptions.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            {presets.length === 0 && (
              <p className="mt-1 text-xs text-muted-foreground">No presets yet. You can create one in settings.</p>
            )}
          </div>

          {/* Info about AI unit classification (per pack: units of the domain) */}
          <div className="md:col-span-2 rounded-2xl border border-primary/20 bg-primary/5 p-4 text-sm">
            <p className="font-medium text-primary">{`📸 AI-Powered ${vocab('Unit', domainKey ?? undefined)} Detection`}</p>
            <p className="mt-1 text-muted-foreground">
              {`After creating the inspection, upload your photos. Our AI will automatically classify them into ${unitsLower} and detect any issues.`}
            </p>
          </div>

          <FormError message={mutation.error ? errorMessage(mutation.error) : null} />
        </Grid>
      </Form>
      </ModalBody>

      {/* Story 2-147xd: `DialogActions` + `ActionDescriptor.htmlType`/`form`
          (grown for exactly this shape — see ActionHosts.tsx's own doc
          comment). The "Create inspection" button is a real
          `type="submit" form="create-inspection-form"` control — it lives
          in `ModalFooter`, outside the `<form>` in `ModalBody` above,
          linked only by the HTML `form` attribute — now expressible
          through the host instead of a hand-grouped bypass. */}
      <ModalFooter>
        <DialogActions
          actions={[
            { id: 'cancel', label: 'Cancel', variant: 'secondary', onClick: onClose, disabled: mutation.isPending },
            {
              id: 'create',
              label: 'Create inspection',
              variant: 'primary',
              htmlType: 'submit',
              form: 'create-inspection-form',
              loading: mutation.isPending,
              disabled: !form.watch('subjectId'),
            },
          ]}
        />
      </ModalFooter>
    </Modal>
  );
}
